/**
 * @file Frontend utilities — expertFlaggedStageBarChart.ts.
 */

import type { CementBoxPlotMetric } from "../components/DataVisualisation/CementBoxPlotChart";
import { REVIEW_STATUS, normalizeReviewStatus } from "../constants/reviewStatus";
import type { ExpertDataset } from "../services/expertDatasetService";
import { formatUnitSymbol } from "./formatUnitSymbol";

/** Type definition — Stage Bar Chart Point. */
export type StageBarChartPoint = {
  stage: string;
  average: number | null;
  individual: number | null;
};

type SummaryKey = "a1Summary" | "a2Summary" | "a3Summary" | "a1a3Summary";

const STAGE_CONFIG: { stage: string; summaryKey: SummaryKey }[] = [
  { stage: "A1", summaryKey: "a1Summary" },
  { stage: "A2", summaryKey: "a2Summary" },
  { stage: "A3", summaryKey: "a3Summary" },
  { stage: "A1-A3", summaryKey: "a1a3Summary" },
];

const toNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number.parseFloat(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const getStageMetricValue = (
  dataset: ExpertDataset,
  summaryKey: SummaryKey,
  metric: CementBoxPlotMetric
): number | null => {
  const summary = (dataset[summaryKey] ?? null) as Record<string, unknown> | null;
  if (!summary) return null;

  const primary =
    metric === "carbon" ? summary.embodiedCarbonValue : summary.embodiedEnergyValue;
  const legacy =
    metric === "carbon" ? summary.embodied_carbon_value : summary.embodied_energy_value;

  return toNumber(primary ?? legacy);
};

const average = (values: number[]): number | null => {
  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

/** Exported function — build Expert Flagged Stage Bar Chart Data. */
export function buildExpertFlaggedStageBarChartData(params: {
  allDatasets: ExpertDataset[];
  metric: CementBoxPlotMetric;
  /** When set, black bars use this submission's stage values. */
  selectedDatasetId?: string | null;
}): StageBarChartPoint[] {
  const { allDatasets, metric, selectedDatasetId } = params;

  const approvedDatasets = allDatasets.filter(
    (d) => normalizeReviewStatus(d.reviewStatus) === REVIEW_STATUS.APPROVED
  );
  const selectedDataset = selectedDatasetId
    ? allDatasets.find((d) => String(d.id) === String(selectedDatasetId)) ?? null
    : null;

  return STAGE_CONFIG.map(({ stage, summaryKey }) => {
    const approvedValues = approvedDatasets
      .map((d) => getStageMetricValue(d, summaryKey, metric))
      .filter((v): v is number => v != null);

    const individual = selectedDataset
      ? getStageMetricValue(selectedDataset, summaryKey, metric)
      : null;

    return {
      stage,
      average: average(approvedValues),
      individual,
    };
  });
}

/** Stable Y-axis cap: averages + every submission in the column (selection must not rescale the chart). */
export function computeStageBarChartYMax(params: {
  chartPoints: StageBarChartPoint[];
  columnDatasets?: ExpertDataset[];
  metric: CementBoxPlotMetric;
}): number {
  const { chartPoints, columnDatasets = [], metric } = params;
  const values: number[] = [];

  for (const row of chartPoints) {
    if (row.average != null) values.push(row.average);
  }

  for (const dataset of columnDatasets) {
    for (const { summaryKey } of STAGE_CONFIG) {
      const value = getStageMetricValue(dataset, summaryKey, metric);
      if (value != null) values.push(value);
    }
  }

  if (values.length === 0) return 1;
  const max = Math.max(...values);
  return max <= 0 ? 1 : Math.ceil(max * 1.15 * 100) / 100;
}

/** Exported function — get Stage Bar Chart Individual Legend Label. */
export function getStageBarChartIndividualLegendLabel(
  selectedSubmissionCode?: string | null
): string {
  if (selectedSubmissionCode) {
    return `Individual (${selectedSubmissionCode})`;
  }
  return STAGE_BAR_CHART_LEGEND.individual;
}

/** Exported function — get Stage Bar Chart Axis Label. */
export function getStageBarChartAxisLabel(metric: CementBoxPlotMetric): string {
  return metric === "carbon"
    ? `Cradle to Gate Embodied Carbon (${formatUnitSymbol("kg CO2eq")}/ kg)`
    : "Cradle to Gate Embodied Energy (MJ/ kg)";
}

/** Exported const — STAGE BAR CHART LEGEND. */
export const STAGE_BAR_CHART_LEGEND = {
  average: "Average (approved datasets)",
  individual: "Individual (filtered flagged dataset)",
} as const;
